// Shared helpers for the "复制档案详情" and "生成登录信息" action buttons
// surfaced on /profiles and /admin/profiles. Kept in one place so the two
// pages stay visually + functionally consistent.

import { profilesAPI } from '../services/api';

const ageOf = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 86400 * 1000));
};

const join = (...parts) => parts.filter(v => v != null && String(v).trim() !== '').join(' · ');

// Try a few common locations to pull a custom-field value off the profile.
//   `customFields` (array of { field_name | field_label, field_value })
//   `gcv`          (object keyed by custom_field_id)
const cf = (p, name, idFallback) => {
  if (Array.isArray(p.customFields)) {
    const hit = p.customFields.find(c => (c.field_name || '').toLowerCase() === name.toLowerCase());
    if (hit && hit.field_value != null && hit.field_value !== '') return hit.field_value;
  }
  if (p.gcv && idFallback != null && p.gcv[idFallback] != null) return p.gcv[idFallback];
  return null;
};

const formatTierObject = (raw) => {
  let v = raw;
  if (typeof v === 'string') {
    const s = v.trim();
    if (s.startsWith('{') || s.startsWith('[')) {
      try { v = JSON.parse(s); } catch { return s; }
    } else {
      return s;
    }
  }
  if (Array.isArray(v)) return v.join('、');
  if (v && typeof v === 'object') {
    if (v.start !== undefined || v.end !== undefined) {
      const a = v.start ?? '';
      const b = v.end ?? '';
      if (a !== '' && b !== '') return `${a} ~ ${b}`;
      if (a !== '') return `≥ ${a}`;
      if (b !== '') return `≤ ${b}`;
      return '';
    }
    const tiers = [];
    if (v.ideal != null && v.ideal !== '') tiers.push(`理想: ${formatTierObject(v.ideal)}`);
    if (v.acceptable != null && v.acceptable !== '') tiers.push(`可接受: ${formatTierObject(v.acceptable)}`);
    if (v.bottom_line != null && v.bottom_line !== '') tiers.push(`底线: ${formatTierObject(v.bottom_line)}`);
    if (tiers.length > 0) return tiers.join(' | ');
    return JSON.stringify(v);
  }
  return String(v);
};

// Build a copy-friendly Chinese plain-text summary of a profile row.
// Tries to include the most informative fields; missing values are skipped
// rather than printed as blanks.
export function buildProfileTextSummary(p, opts = {}) {
  if (!p) return '';
  const name = `${p.first_name || ''} ${p.last_name || ''}`.trim() || '(未命名)';
  const age = ageOf(p.date_of_birth || p.birth_date);
  const offlineId = p.offline_id || (p.gcv && p.gcv['115']) || p.id;
  const lines = [];

  lines.push(`【档案】${name}${age != null ? ` · ${age}岁` : ''}`);
  lines.push(`线下ID: ${offlineId}`);
  if (p.gender) lines.push(`性别: ${p.gender === 'male' ? '男' : p.gender === 'female' ? '女' : p.gender}`);
  const loc = join(p.location_city || p.city, p.location_state || p.state, p.location_country || p.country);
  if (loc) lines.push(`位置: ${loc}`);
  if (p.email && !opts.excludeEmail) lines.push(`邮箱: ${p.email}`);
  if (p.phone && !opts.excludePhone) lines.push(`电话: ${p.phone}`);
  if (p.occupation) lines.push(`职业: ${p.occupation}`);

  // Common custom-field highlights (matches the IDs surfaced elsewhere).
  const facts = [
    ['身高',     cf(p, 'height', 9),          v => `${v}cm`],
    ['体重',     cf(p, 'weight'),             v => `${v}kg`],
    ['学历',     cf(p, 'edu') || cf(p, 'education') || p.education],
    ['婚姻',     cf(p, 'marital_status', 80) || cf(p, 'love')],
    ['子女',     cf(p, 'children_status', 90) || cf(p, 'child')],
    ['月收入',   cf(p, 'monthly_income', 109) || cf(p, 'pay')],
    ['房产',     cf(p, 'real_estate', 6)],
    ['星座',     p.star_sign],
  ];
  facts.forEach(([label, val, fmt]) => {
    if (val == null || val === '') return;
    lines.push(`${label}: ${fmt ? fmt(val) : val}`);
  });

  if (p.bio) {
    lines.push('');
    lines.push('自我介绍:');
    lines.push(String(p.bio).trim());
  }

  // Mate preferences (3-tier) — dump every mate_* customField.
  const matePrefs = Array.isArray(p.customFields)
    ? p.customFields.filter(c => (c.field_name || '').toLowerCase().startsWith('mate_'))
    : [];
  if (matePrefs.length > 0) {
    lines.push('');
    lines.push('择偶要求:');
    matePrefs.forEach(c => {
      const label = c.field_label_zh || c.field_label || c.field_name;
      const val = formatTierObject(c.field_value);
      if (val) lines.push(`  · ${label}: ${val}`);
    });
  }

  // Risk / disclaimer note appended to every copied profile.
  lines.push('');
  lines.push('风险提示：资料均由被推荐人本人提供，本公司不对其真实性作担保。如有需要，可委托合作律师或第三方机构进行背景调查。');

  return lines.join('\n');
}

// Best-effort clipboard write. The execCommand fallback needs a fresh
// user gesture so we focus the textarea explicitly and place it visibly
// (off-screen is sometimes blocked). Also handles the case where the
// caller awaited a network request between the click and the copy —
// modern browsers may revoke clipboard permission past that boundary.
export async function copyToClipboard(text) {
  if (!text) return false;
  // Primary path — async Clipboard API (works on HTTPS / localhost).
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // Don't return — fall through to legacy execCommand attempt.
    if (typeof console !== 'undefined') console.warn('[copyToClipboard] clipboard API failed:', e?.message);
  }
  // Legacy fallback — synchronous, requires textarea to be in the DOM
  // and focused / selected. Some browsers block when textarea is off-
  // screen with `left: -9999px`, so we use opacity:0 in viewport.
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.opacity = '0';
    ta.style.pointerEvents = 'none';
    document.body.appendChild(ta);
    ta.focus({ preventScroll: true });
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand && document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) return true;
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('[copyToClipboard] execCommand failed:', e?.message);
  }
  return false;
}

// Last-resort UI when both clipboard paths fail — opens a small modal
// with the text in a pre-selected textarea so the user can hit Ctrl+C /
// Cmd+C manually. The dialog's own Copy button re-runs the clipboard
// write inside a fresh user gesture (which often succeeds where the
// initial post-fetch attempt didn't, since the click is "live" again).
export function openCopyFallbackDialog(text, { isZh = true } = {}) {
  if (!text || typeof document === 'undefined') return;
  // Tear down any previous dialog so repeat-clicks don't stack.
  const existing = document.getElementById('__profileShare_copy_dialog');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = '__profileShare_copy_dialog';
  Object.assign(overlay.style, {
    position: 'fixed', inset: '0', background: 'rgba(15,23,42,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: '25000', padding: '16px',
  });
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

  const card = document.createElement('div');
  Object.assign(card.style, {
    background: '#fff', borderRadius: '12px',
    width: 'min(560px, 100%)', maxHeight: '80vh',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
  });

  const header = document.createElement('div');
  Object.assign(header.style, {
    padding: '14px 18px', borderBottom: '1px solid #E8E4E0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontFamily: 'inherit', fontSize: '14px', fontWeight: '700', color: '#1F2937',
  });
  header.textContent = isZh ? '复制档案详情' : 'Copy profile';

  const closeBtn = document.createElement('button');
  closeBtn.textContent = '×';
  Object.assign(closeBtn.style, {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '20px', color: '#9CA3AF', lineHeight: '1',
  });
  closeBtn.addEventListener('click', () => overlay.remove());
  header.appendChild(closeBtn);

  const hint = document.createElement('div');
  Object.assign(hint.style, {
    padding: '10px 18px', fontSize: '12px', color: '#92400E',
    background: '#FFFBEB', borderBottom: '1px solid #FDE68A',
  });
  hint.textContent = isZh
    ? '自动复制未成功 — 请按 Ctrl+C(或 ⌘+C)手动复制下方内容。'
    : 'Auto-copy failed — press Ctrl+C (or ⌘+C) to copy the text below manually.';

  const ta = document.createElement('textarea');
  ta.value = text;
  Object.assign(ta.style, {
    flex: '1', margin: '14px 18px',
    minHeight: '160px',
    padding: '10px', border: '1px solid #E5E7EB', borderRadius: '8px',
    fontSize: '12px', fontFamily: 'monospace', resize: 'vertical',
    outline: 'none', background: '#FAFAF8', color: '#1F2937',
  });

  const footer = document.createElement('div');
  Object.assign(footer.style, {
    padding: '10px 18px 14px', display: 'flex', justifyContent: 'flex-end', gap: '8px',
  });
  const copyBtn = document.createElement('button');
  copyBtn.textContent = isZh ? '尝试复制' : 'Try copy';
  Object.assign(copyBtn.style, {
    padding: '6px 14px', borderRadius: '6px', border: '1px solid #722F37',
    background: '#722F37', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  });
  copyBtn.addEventListener('click', async () => {
    const ok = await copyToClipboard(text);
    copyBtn.textContent = ok
      ? (isZh ? '已复制 ✓' : 'Copied ✓')
      : (isZh ? '仍失败,请手动复制' : 'Still failed, copy manually');
    if (ok) setTimeout(() => overlay.remove(), 600);
  });
  const closeFooterBtn = document.createElement('button');
  closeFooterBtn.textContent = isZh ? '关闭' : 'Close';
  Object.assign(closeFooterBtn.style, {
    padding: '6px 14px', borderRadius: '6px', border: '1px solid #E5E7EB',
    background: '#fff', color: '#374151', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  });
  closeFooterBtn.addEventListener('click', () => overlay.remove());
  footer.appendChild(copyBtn);
  footer.appendChild(closeFooterBtn);

  card.appendChild(header);
  card.appendChild(hint);
  card.appendChild(ta);
  card.appendChild(footer);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
  // Auto-select text so the user just needs Ctrl+C.
  setTimeout(() => { try { ta.focus(); ta.select(); ta.setSelectionRange(0, ta.value.length); } catch (_) {} }, 0);
}

// Convenience wrapper used by the row-action buttons. Tries copy,
// falls back to the manual dialog. Always returns a boolean so the
// caller can show its own toast.
export async function copyOrShowDialog(text, opts = {}) {
  const ok = await copyToClipboard(text);
  if (ok) return true;
  openCopyFallbackDialog(text, opts);
  return false;
}

// Generate / reset a client portal login for `profileId`. Returns
// { email, password, login_url } or throws.
export async function generateLoginForProfile(profileId) {
  const r = await profilesAPI.generateLogin(profileId);
  return r.data || {};
}
